import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, Button } from '../../components/ui'
import { useAuth } from '../../components/AuthProvider'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import { ChevronLeft } from 'lucide-react'
import { pageScope } from '../../lib/cssScope'
import { TpsmProfileSectionTabs, type TpsmProfileSectionTab } from '../../components/tpsm/TpsmProfileSectionTabs'
import { SupplierComplianceSummaryPanel } from '../../components/tpsm/SupplierComplianceSummaryPanel'
import { SupplierStandardsExplorerPanel } from '../../components/tpsm/SupplierStandardsExplorerPanel'
import { USE_MOCK_TPSM_INVENTORY_DATA } from '../../mocks/useMockTpsmInventory'
import tpsmSuppliersFixture from '../../mocks/fixtures/tpsm-suppliers.json'
import tpsmCriticalityFixture from '../../mocks/fixtures/tpsm-criticality.json'
import { fetchTpsmAssessmentsForSupplier } from '../../mocks/fetchTpsmAssessmentsForSupplier'

type Supplier = {
  id: string
  name: string
  contactName?: string
  email?: string
  website?: string
  categoryAccess: string
  supplierType: string
  standards: string[]
  compliance: number
  lastAssessment?: string
  criticality?: string
  criticalityScore?: number
  criticalityLevel?: string
}

type Assessment = {
  id: string
  standardCode: string
  status: string
  complianceScore: number
  assessedAt?: string
  updatedAt?: string
  findings?: any
}

type Incident = {
  id: string
  type: string
  severity: string
  occurredAt: string
  description?: string
}

type SecurityPosture = {
  encryptionUsed?: boolean
  mfaUsed?: boolean
  ssdlcProcess?: boolean
  sastDast?: boolean
  infrastructureScanners?: boolean
  usesCloud?: boolean
}

const SupplierProfile: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [supplier, setSupplier] = useState<Supplier | null>(null)
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [securityPosture, setSecurityPosture] = useState<SecurityPosture | null>(null)
  const [criticalityData, setCriticalityData] = useState<any>(null)
  const [profileTab, setProfileTab] = useState<TpsmProfileSectionTab>('compliance')

  useEffect(() => {
    if (supplierId) {
      fetchSupplierData()
    }
  }, [supplierId, token])

  const fetchSupplierData = async () => {
    setLoading(true)
    setError('')
    try {
      if (USE_MOCK_TPSM_INVENTORY_DATA && supplierId) {
        const list = (tpsmSuppliersFixture as { suppliers?: Supplier[] }).suppliers
        const foundSupplier = Array.isArray(list) ? list.find((s) => s.id === supplierId) : null
        if (!foundSupplier) {
          setError('Supplier not found')
          return
        }
        setSupplier(foundSupplier)
        const loadedAssessments = (await fetchTpsmAssessmentsForSupplier(supplierId, token)) as Assessment[]
        setAssessments(loadedAssessments)
        const securityPostureAssessment = loadedAssessments.find((a: any) => a.standardCode === 'SECURITY_POSTURE')
        if (securityPostureAssessment?.findings?.metadata?.securityPosture) {
          setSecurityPosture(securityPostureAssessment.findings.metadata.securityPosture)
        } else {
          setSecurityPosture(null)
        }
        setIncidents([])
        const critRows = (tpsmCriticalityFixture as { suppliers?: Array<{ supplierId: string; criticalityScore?: number; criticalityLevel?: string }> }).suppliers
        const row = Array.isArray(critRows) ? critRows.find((r) => r.supplierId === supplierId) : null
        if (row) {
          setCriticalityData(row)
          setSupplier((prev) =>
            prev
              ? {
                  ...prev,
                  criticalityScore: row.criticalityScore,
                  criticalityLevel: row.criticalityLevel,
                }
              : null,
          )
        } else {
          setCriticalityData(null)
        }
        return
      }

      // Fetch supplier details
      const suppliersRes = await fetch(API_ENDPOINTS.SUPPLIERS, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const suppliersData = await safeJson(suppliersRes)
      const foundSupplier = Array.isArray(suppliersData.suppliers) 
        ? suppliersData.suppliers.find((s: any) => s.id === supplierId)
        : null
      
      if (!foundSupplier) {
        setError('Supplier not found')
        return
      }
      setSupplier(foundSupplier)

      // Fetch assessments
      const assessmentsRes = await fetch(`${API_ENDPOINTS.TPSM_ASSESSMENTS}/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const assessmentsData = await safeJson(assessmentsRes)
      const loadedAssessments = Array.isArray(assessmentsData) ? assessmentsData : []
      setAssessments(loadedAssessments)

      // Extract Security Posture from assessments
      const securityPostureAssessment = loadedAssessments.find((a: any) => a.standardCode === 'SECURITY_POSTURE')
      if (securityPostureAssessment?.findings?.metadata?.securityPosture) {
        setSecurityPosture(securityPostureAssessment.findings.metadata.securityPosture)
      }

      // Fetch incidents
      const incidentsRes = await fetch(`${API_ENDPOINTS.TPSM_INCIDENTS_SUPPLIER(supplierId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const incidentsData = await safeJson(incidentsRes)
      setIncidents(Array.isArray(incidentsData) ? incidentsData : [])

      // Fetch criticality score
      try {
        const criticalityRes = await fetch(`${API_ENDPOINTS.TPSM_CRITICALITY_SUPPLIER(supplierId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const criticalityData = await safeJson(criticalityRes)
        setCriticalityData(criticalityData)
        if (criticalityData.criticalityScore !== undefined) {
          setSupplier(prev => prev ? {
            ...prev,
            criticalityScore: criticalityData.criticalityScore,
            criticalityLevel: criticalityData.criticalityLevel
          } : null)
        }
      } catch (e) {
        console.warn('Failed to fetch criticality:', e)
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load supplier data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={`${pageScope('tpsm-supplier-profile')} space-y-6`}>
        <Card className="metric-card">
          <CardContent className="p-8">
            <div className="text-center">{t('common.loading', 'Loading...')}</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !supplier) {
    return (
      <div className={`${pageScope('tpsm-supplier-profile')} space-y-6`}>
        <Card className="metric-card">
          <CardContent className="p-8">
            <div className="text-center text-red-600">{error || 'Supplier not found'}</div>
            <div className="mt-4 flex justify-center">
            <Button
              color="secondary"
              onClick={() => navigate('/tpsm/inventory')}
            >
              {t('common.back', 'Back to Inventory')}
            </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={`${pageScope('tpsm-supplier-profile')} space-y-6`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            color="secondary"
            onClick={() => navigate('/tpsm/inventory')}
          >
            <ChevronLeft />
            {t('common.back', 'Back')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{supplier.name}</h1>
            <p className="text-sm text-gray-600">
              {supplier.email && `${supplier.email} • `}
              {supplier.categoryAccess || 'No category'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded text-sm font-medium ${
            supplier.criticalityLevel === 'LOW' ? 'bg-green-100 text-green-700' :
            supplier.criticalityLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
            supplier.criticalityLevel === 'HIGH' ? 'bg-orange-100 text-orange-700' :
            supplier.criticalityLevel === 'CRITICAL' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {supplier.criticalityLevel || 'NOT_ASSESSED'}
            {supplier.criticalityScore !== undefined && ` (${supplier.criticalityScore.toFixed(1)})`}
          </span>
        </div>
      </div>

      <TpsmProfileSectionTabs
        active={profileTab}
        onChange={setProfileTab}
        complianceLabel={t('tpsm.supplierProfile.viewCompliance', 'Compliance Summary')}
        standardsLabel={t('tpsm.supplierProfile.viewStandards', 'Standards Explorer')}
      />

      <div className="mt-4">
        {profileTab === 'compliance' && (
          <SupplierComplianceSummaryPanel
            supplierId={supplier.id}
            supplierName={supplier.name}
            enabled
          />
        )}
        {profileTab === 'standards' && (
          <SupplierStandardsExplorerPanel
            supplierId={supplier.id}
            supplierName={supplier.name}
            enabled
          />
        )}
      </div>
    </div>
  )
}

export default SupplierProfile

